// src/components/CobrancaPendenciasScreen.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Ban,
  CheckCircle2,
  CircleDollarSign,
  CreditCard,
  LoaderCircle,
  Menu,
  Pause,
  Play,
  Plus,
  ReceiptText,
  RefreshCw,
  Save,
  Send,
  Settings2,
  ShieldCheck,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";

import AppSidebar from "./AppSidebar";
import Logo from "../assets/logo.webp";
import { useToast } from "../context/ToastContext";

import "../sass/pages/cobrancaPendencias.scss";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
  PLAN: "plano",
  STATUS_ASSINATURA: "statusAssinatura",
  ASSINATURA_ATIVA: "assinaturaAtiva",
  RECURSOS: "recursos",
};

const STATUS_OPTIONS = [
  { value: "PENDENTES", label: "Pendentes" },
  { value: "ABERTA", label: "Abertas" },
  { value: "PAUSADA", label: "Pausadas" },
  { value: "PAGA", label: "Pagas" },
  { value: "CANCELADA", label: "Canceladas" },
  { value: "", label: "Todas" },
];

const FORMA_PAGAMENTO_OPTIONS = [
  "Pix",
  "Dinheiro",
  "Cartão de crédito",
  "Cartão de débito",
  "Transferência",
  "Outro",
];

function readAuth(key) {
  return sessionStorage.getItem(key) || localStorage.getItem(key) || "";
}

function saveAuthToken(token) {
  if (!token) return;

  try {
    localStorage.setItem(AUTH_KEYS.TOKEN, token);
    sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
  } catch {
    // ignora storage indisponível
  }
}

function normalizeText(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function parseJsonStorage(value, fallback = {}) {
  try {
    if (!value) return fallback;

    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function isPremiumByPlan(plano) {
  const value = normalizeText(plano);

  return (
    value === "premium" ||
    value === "pro" ||
    value === "psyassist premium" ||
    value.includes("premium")
  );
}

function isStatusLiberado(statusAssinatura) {
  const status = normalizeText(statusAssinatura || "active");
  return ["active", "trialing", "past_due"].includes(status);
}

function isAssinaturaAtiva(value) {
  if (value === "" || value === null || value === undefined) return true;
  return String(value).trim().toLowerCase() !== "false";
}

function canUseReguaAutomatica() {
  const recursos = parseJsonStorage(readAuth(AUTH_KEYS.RECURSOS), {});

  if (recursos?.cobrancas === true || recursos?.reguaCobranca === true) {
    return true;
  }

  return (
    isPremiumByPlan(readAuth(AUTH_KEYS.PLAN)) &&
    isStatusLiberado(readAuth(AUTH_KEYS.STATUS_ASSINATURA)) &&
    isAssinaturaAtiva(readAuth(AUTH_KEYS.ASSINATURA_ATIVA))
  );
}

function getObjectId(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value?._id || value?.id || "";
}

function onlyDigits(value = "") {
  return String(value || "").replace(/\D/g, "");
}

function parseMoneyBR(value) {
  if (value === null || value === undefined || value === "") return 0;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const normalized = String(value)
    .replace(/[R$\s]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatMoneyBR(value) {
  const number = Number(value || 0);

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number.isFinite(number) ? number : 0);
}

function formatMoneyInput(value) {
  const digits = onlyDigits(value).slice(0, 10);

  if (!digits) return "";

  const cents = Number(digits || "0") / 100;

  return cents.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDateBR(value) {
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Não informado";

    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  } catch {
    return "Não informado";
  }
}

function formatDateTimeBR(value) {
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Não informado";

    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return "Não informado";
  }
}

function toInputDateTime(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";

  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60 * 1000);

  return local.toISOString().slice(0, 16);
}

function getStatusLabel(status) {
  const normalized = String(status || "").trim().toUpperCase();

  if (normalized === "ABERTA") return "Aberta";
  if (normalized === "PAUSADA") return "Pausada";
  if (normalized === "PAGA") return "Paga";
  if (normalized === "CANCELADA") return "Cancelada";
  if (normalized === "ENCERRADA") return "Encerrada";

  return status || "Não informado";
}

function getEtapaLabel(pendencia) {
  const dias = Number(pendencia?.proximaEtapaDias);
  const ordem = Number(pendencia?.proximaEtapaOrdem);

  if (Number.isFinite(dias) && dias > 0) {
    return `Etapa ${ordem || ""} • D+${dias}`.replace("  ", " ");
  }

  const normalized = String(pendencia?.proximaEtapa || "").trim().toUpperCase();

  if (/^D\d+$/.test(normalized)) return normalized.replace("D", "D+");
  if (pendencia?.reguaFinalizada) return "Finalizada";

  return "Sem próximo envio";
}

async function readErrorMessage(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const json = await response.json();
      return json?.message || json?.detail || json?.error || JSON.stringify(json);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function normalizeCommunicationText(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

const CHANNEL_FAILURE_STATUS = new Set([
  "failed",
  "undelivered",
  "canceled",
  "cancelled",
  "error",
  "erro",
  "falhou",
]);

const CHANNEL_SUCCESS_STATUS = new Set([
  "created",
  "queued",
  "sent",
  "delivered",
  "read",
  "success",
  "sucesso",
  "ok",
  "enviado",
  "entregue",
  "lido",
  "encaminhado",
]);

function normalizeChannelKey(value = "") {
  const normalized = normalizeCommunicationText(value).replace(/[^a-z]/g, "");

  if (normalized.includes("whatsapp")) return "whatsapp";
  if (normalized.includes("email") || normalized.includes("mail")) return "email";

  return normalized;
}

function findChannelResult(payload, channel) {
  if (!payload || typeof payload !== "object") return null;

  const target = normalizeChannelKey(channel);

  const containers = [
    payload,
    payload?.resultados,
    payload?.resultado,
    payload?.canais,
    payload?.comunicacao,
    payload?.comunicacoes,
    payload?.envios,
    payload?.termo,
    payload?.lembrete,
    payload?.cobranca,
    payload?.recibo,
    payload?.termo?.resultados,
    payload?.lembrete?.resultados,
    payload?.cobranca?.resultados,
    payload?.recibo?.resultados,
    payload?.recibo?.comunicacao,
    payload?.recibo?.comunicacoes,
  ].filter(Boolean);

  for (const container of containers) {
    if (Array.isArray(container)) {
      for (const item of container) {
        if (!item || typeof item !== "object") continue;

        const nestedDirect =
          target === "whatsapp"
            ? item.whatsapp ||
              item.whatsApp ||
              item.resultadoWhatsapp ||
              item.whatsappResultado
            : item.email ||
              item.eMail ||
              item.resultadoEmail ||
              item.emailResultado;

        if (
          nestedDirect !== undefined &&
          nestedDirect !== null &&
          (typeof nestedDirect === "object" ||
            typeof nestedDirect === "boolean" ||
            CHANNEL_SUCCESS_STATUS.has(
              normalizeCommunicationText(nestedDirect).replace(/\s+/g, "_")
            ) ||
            CHANNEL_FAILURE_STATUS.has(
              normalizeCommunicationText(nestedDirect).replace(/\s+/g, "_")
            ))
        ) {
          return nestedDirect;
        }

        const key = normalizeChannelKey(
          item?.canal ||
            item?.channel ||
            item?.tipoCanal ||
            item?.tipo ||
            item?.nome
        );

        if (key === target) return item;
      }

      continue;
    }

    if (typeof container !== "object") continue;

    const direct =
      target === "whatsapp"
        ? container.whatsapp ||
          container.whatsApp ||
          container.resultadoWhatsapp ||
          container.whatsappResultado
        : container.email ||
          container.eMail ||
          container.resultadoEmail ||
          container.emailResultado;

    if (direct !== undefined && direct !== null) {
      return direct;
    }

    const ownKey = normalizeChannelKey(
      container?.canal ||
        container?.channel ||
        container?.tipoCanal
    );

    if (ownKey === target) {
      return container;
    }
  }

  return null;
}

function normalizeChannelResult(payload, channel) {
  const raw = findChannelResult(payload, channel);

  if (raw === null || raw === undefined) return null;

  if (typeof raw === "boolean") {
    return {
      channel,
      success: raw,
      failed: !raw,
      status: raw ? "sent" : "failed",
      error: "",
    };
  }

  if (typeof raw === "string") {
    const status = normalizeCommunicationText(raw).replace(/\s+/g, "_");

    return {
      channel,
      success: CHANNEL_SUCCESS_STATUS.has(status),
      failed: CHANNEL_FAILURE_STATUS.has(status),
      status,
      error: "",
    };
  }

  if (typeof raw !== "object") return null;

  const status = normalizeCommunicationText(
    raw.status ||
      raw.messageStatus ||
      raw.twilioStatus ||
      raw.situacao ||
      ""
  ).replace(/\s+/g, "_");

  const successFlag =
    raw.success ??
    raw.sucesso ??
    raw.ok ??
    raw.enviado ??
    raw.sent ??
    null;

  const requested =
    raw.solicitado ??
    raw.requested ??
    raw.processado ??
    true;

  const failed =
    requested !== false &&
    (successFlag === false || CHANNEL_FAILURE_STATUS.has(status));

  const success =
    requested !== false &&
    !failed &&
    (successFlag === true || CHANNEL_SUCCESS_STATUS.has(status));

  return {
    channel,
    success,
    failed,
    status,
    requested,
    error:
      raw.erroMensagem ||
      raw.erro ||
      raw.error ||
      raw.message ||
      "",
  };
}

function getChannelStatusLabel(result) {
  if (!result) return "";

  if (result.requested === false) return "Não solicitado";

  const status = normalizeCommunicationText(result.status).replace(/\s+/g, "_");

  if (result.failed) return "Falhou";

  if (result.channel === "whatsapp") {
    if (status === "read" || status === "lido") return "Lido";
    if (status === "delivered" || status === "entregue") return "Entregue";
    if (status === "sent" || status === "enviado") return "Enviado";
    if (
      status === "queued" ||
      status === "created" ||
      status === "encaminhado"
    ) {
      return "Encaminhado";
    }

    if (result.success) return "Encaminhado";
    return "Processando";
  }

  if (result.success) return "Enviado";
  if (result.failed) return "Falhou";

  return "Processando";
}

function buildCommunicationFeedback(payload, fallbackMessage = "") {
  const email = normalizeChannelResult(payload, "email");
  const whatsapp = normalizeChannelResult(payload, "whatsapp");
  const results = [email, whatsapp].filter(Boolean);

  if (!results.length) {
    return {
      hasChannels: false,
      tone: "success",
      message: fallbackMessage,
      details: [],
    };
  }

  const details = results.map((result) => {
    const label = result.channel === "whatsapp" ? "WhatsApp" : "E-mail";
    const statusLabel = getChannelStatusLabel(result);

    if (result.failed && result.error) {
      return `${label}: ${statusLabel} — ${result.error}`;
    }

    return `${label}: ${statusLabel}`;
  });

  const hasSuccess = results.some((item) => item.success);
  const hasFailure = results.some((item) => item.failed);

  return {
    hasChannels: true,
    tone: hasFailure ? (hasSuccess ? "warning" : "error") : "success",
    message: details.join(" • "),
    details,
  };
}


function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  tone = "blue",
}) {
  return (
    <article className={`cp-stat cp-stat--${tone}`}>
      <div className="cp-stat__icon">
        <Icon size={18} aria-hidden="true" />
      </div>

      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        {hint ? <small>{hint}</small> : null}
      </div>
    </article>
  );
}

function AutomationCard({
  hasPremiumAutomation,
  configuracao,
  loading,
  onConfigure,
  onUpgrade,
}) {
  const etapas = Array.isArray(configuracao?.etapas)
    ? configuracao.etapas.filter((item) => item?.ativa !== false)
    : [];

  const hasConfiguration =
    configuracao &&
    typeof configuracao === "object";

  const active =
    hasConfiguration &&
    configuracao?.ativa !== false;

  return (
    <aside
      className={`cp-hero-card ${
        hasPremiumAutomation
          ? "cp-hero-card--premium"
          : "cp-hero-card--locked"
      }`}
    >
      <div className="cp-hero-card__top">
        <div className="cp-hero-card__icon">
          {hasPremiumAutomation ? (
            <Sparkles size={20} aria-hidden="true" />
          ) : (
            <ShieldCheck size={20} aria-hidden="true" />
          )}
        </div>

        <span>
          {hasPremiumAutomation
            ? "Automação Premium"
            : "Recurso Premium"}
        </span>
      </div>

      {hasPremiumAutomation ? (
        <>
          <strong>
            {loading
              ? "Carregando régua..."
              : !hasConfiguration
                ? "Régua ainda não configurada"
                : active
                  ? "Régua automática ativa"
                  : "Régua automática pausada"}
          </strong>

          <p>
            {loading
              ? "Consultando a configuração atual."
              : etapas.length
                ? `${etapas.length} etapa(s) ativa(s) configurada(s) para os lembretes.`
                : "Configure as etapas de cobrança automática para os pagamentos pendentes."}
          </p>

          <button
            type="button"
            onClick={onConfigure}
            disabled={loading}
          >
            <Settings2 size={15} aria-hidden="true" />
            Configurar régua
          </button>
        </>
      ) : (
        <>
          <strong>Cobranças automáticas</strong>

          <p>
            O registro manual de pagamento continua liberado. Régua
            configurável e comunicação multicanal fazem parte do Premium.
          </p>

          <button type="button" onClick={onUpgrade}>
            Conhecer Premium
          </button>
        </>
      )}
    </aside>
  );
}

function EmptyState({ status }) {
  return (
    <div className="cp-empty">
      <div className="cp-empty__icon">
        <CreditCard size={26} aria-hidden="true" />
      </div>

      <strong>Nenhuma pendência encontrada</strong>

      <span>
        {status === "PENDENTES"
          ? "Não existem sessões concluídas aguardando pagamento."
          : status
            ? "Não existem cobranças com o filtro selecionado."
            : "As sessões concluídas sem pagamento e sem receita aparecerão aqui automaticamente."}
      </span>
    </div>
  );
}

function PaymentModal({
  pendencia,
  open,
  onClose,
  onConfirm,
  saving,
}) {
  const [valor, setValor] = useState("");
  const [formaPagamento, setFormaPagamento] = useState("Pix");
  const [dataPagamento, setDataPagamento] = useState(
    toInputDateTime(new Date())
  );
  const [observacao, setObservacao] = useState("");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open || !pendencia) return;

    setValor(
      formatMoneyInput(
        String(
          Math.round(
            Number(pendencia.valor || 0) * 100
          )
        )
      )
    );
    setFormaPagamento("Pix");
    setDataPagamento(toInputDateTime(new Date()));
    setObservacao("");
    setFormError("");
  }, [open, pendencia]);

  if (!open || !pendencia) return null;

  const handleSubmit = () => {
    const valorNumber = parseMoneyBR(valor);

    if (valorNumber <= 0) {
      setFormError("Informe um valor válido.");
      return;
    }

    if (!dataPagamento) {
      setFormError("Informe a data do pagamento.");
      return;
    }

    setFormError("");

    onConfirm({
      valor: valorNumber,
      formaPagamento,
      dataPagamento: new Date(dataPagamento).toISOString(),
      observacao,
    });
  };

  return (
    <div
      className="cp-modal-backdrop"
      role="presentation"
      onMouseDown={onClose}
    >
      <section
        className="cp-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Informar pagamento"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="cp-modal__head">
          <div className="cp-modal__title">
            <span>Pagamento</span>
            <strong>Informar pagamento</strong>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="cp-modal__body">
          <div className="cp-client-summary">
            <div className="cp-client-summary__icon">
              <CircleDollarSign size={19} />
            </div>

            <div>
              <strong>
                {pendencia.clienteNome || "Cliente"}
              </strong>

              <span>
                Sessão em {formatDateBR(pendencia.dataSessao)}
                {" • "}
                {pendencia.tipoSessao || "Atendimento"}
              </span>
            </div>
          </div>

          {formError ? (
            <div className="cp-form-error" role="alert">
              <AlertCircle size={16} />
              {formError}
            </div>
          ) : null}

          <label className="cp-field">
            <span>Valor pago</span>

            <input
              value={valor}
              onChange={(event) => {
                setValor(formatMoneyInput(event.target.value));
                if (formError) setFormError("");
              }}
              placeholder="0,00"
              inputMode="numeric"
              disabled={saving}
            />
          </label>

          <label className="cp-field">
            <span>Forma de pagamento</span>

            <select
              value={formaPagamento}
              onChange={(event) =>
                setFormaPagamento(event.target.value)
              }
              disabled={saving}
            >
              {FORMA_PAGAMENTO_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          <label className="cp-field">
            <span>Data do pagamento</span>

            <input
              type="datetime-local"
              value={dataPagamento}
              onChange={(event) => {
                setDataPagamento(event.target.value);
                if (formError) setFormError("");
              }}
              disabled={saving}
            />
          </label>

          <label className="cp-field">
            <span>Observação</span>

            <textarea
              value={observacao}
              onChange={(event) =>
                setObservacao(event.target.value)
              }
              placeholder="Ex: pagamento confirmado por Pix"
              rows={3}
              disabled={saving}
            />
          </label>
        </div>

        <div className="cp-modal__actions">
          <button
            className="cp-btn cp-btn--ghost"
            type="button"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>

          <button
            className="cp-btn cp-btn--success"
            type="button"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? (
              <>
                <LoaderCircle
                  size={16}
                  className="cp-spin"
                />
                Salvando...
              </>
            ) : (
              <>
                <CheckCircle2 size={16} />
                Confirmar pagamento
              </>
            )}
          </button>
        </div>
      </section>
    </div>
  );
}


function ReguaConfigModal({
  open,
  configuracao,
  onClose,
  onSave,
  saving,
}) {
  const [ativa, setAtiva] = useState(true);
  const [etapas, setEtapas] = useState([]);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open) return;

    const source = Array.isArray(configuracao?.etapas)
      ? configuracao.etapas
      : [];

    setAtiva(configuracao?.ativa !== false);
    setFormError("");

    setEtapas(
      source.map((item, index) => ({
        _id: item?._id || undefined,
        ordem: index + 1,
        diasAposSessao: Number(item?.diasAposSessao || 1),
        horario: item?.horario || "09:00",
        assunto: item?.assunto || "Lembrete de pagamento",
        mensagem:
          item?.mensagem ||
          "Identificamos uma pendência financeira referente a um atendimento anterior.",
        ativa: item?.ativa !== false,
      }))
    );
  }, [configuracao, open]);

  if (!open) return null;

  const atualizarEtapa = (index, field, value) => {
    if (formError) setFormError("");

    setEtapas((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index
          ? { ...item, [field]: value }
          : item
      )
    );
  };

  const adicionarEtapa = () => {
    if (etapas.length >= 10) {
      setFormError("A régua permite no máximo 10 etapas.");
      return;
    }

    const ultimoDia = etapas.length
      ? Number(
          etapas[etapas.length - 1]?.diasAposSessao || 0
        )
      : 0;

    setFormError("");

    setEtapas((current) => [
      ...current,
      {
        ordem: current.length + 1,
        diasAposSessao: ultimoDia + 7 || 1,
        horario: "09:00",
        assunto: "Lembrete de pagamento",
        mensagem:
          "Identificamos uma pendência financeira referente a um atendimento anterior.",
        ativa: true,
      },
    ]);
  };

  const removerEtapa = (index) => {
    if (etapas.length <= 1) {
      setFormError("Mantenha pelo menos uma etapa configurada.");
      return;
    }

    setFormError("");

    setEtapas((current) =>
      current
        .filter((_, itemIndex) => itemIndex !== index)
        .map((item, itemIndex) => ({
          ...item,
          ordem: itemIndex + 1,
        }))
    );
  };

  const moverEtapa = (index, offset) => {
    const destino = index + offset;

    if (destino < 0 || destino >= etapas.length) return;

    setFormError("");

    setEtapas((current) => {
      const next = [...current];
      const [item] = next.splice(index, 1);

      next.splice(destino, 0, item);

      return next.map((etapa, itemIndex) => ({
        ...etapa,
        ordem: itemIndex + 1,
      }));
    });
  };

  const handleSave = () => {
    const normalizadas = etapas.map((item, index) => ({
      ...item,
      ordem: index + 1,
      diasAposSessao: Number(item.diasAposSessao),
      horario: String(item.horario || "09:00"),
      assunto: String(item.assunto || "").trim(),
      mensagem: String(item.mensagem || "").trim(),
      ativa: item.ativa !== false,
    }));

    if (!normalizadas.length) {
      setFormError("Adicione pelo menos uma etapa.");
      return;
    }

    for (const etapa of normalizadas) {
      if (
        !Number.isInteger(etapa.diasAposSessao) ||
        etapa.diasAposSessao < 1 ||
        etapa.diasAposSessao > 365
      ) {
        setFormError(
          "Os dias de cada etapa devem estar entre 1 e 365."
        );
        return;
      }

      if (!etapa.assunto || !etapa.mensagem) {
        setFormError(
          `Preencha o assunto e a mensagem da etapa ${etapa.ordem}.`
        );
        return;
      }
    }

    const dias = normalizadas.map(
      (item) => item.diasAposSessao
    );

    if (new Set(dias).size !== dias.length) {
      setFormError(
        "Não é permitido repetir o mesmo D+ em duas etapas."
      );
      return;
    }

    for (
      let index = 1;
      index < normalizadas.length;
      index += 1
    ) {
      if (
        normalizadas[index].diasAposSessao <=
        normalizadas[index - 1].diasAposSessao
      ) {
        setFormError(
          "Organize as etapas em ordem crescente de dias."
        );
        return;
      }
    }

    setFormError("");
    onSave({ ativa, etapas: normalizadas });
  };

  return (
    <div
      className="cp-modal-backdrop"
      role="presentation"
      onMouseDown={onClose}
    >
      <section
        className="cp-modal cp-modal--large"
        role="dialog"
        aria-modal="true"
        aria-label="Configurar régua de cobrança"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="cp-modal__head">
          <div className="cp-modal__title">
            <span>Automação Premium</span>
            <strong>Configurar régua de cobrança</strong>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        <div className="cp-modal__body cp-regua-editor">
          <label className="cp-switch-row">
            <div>
              <strong>Régua automática ativa</strong>
              <span>
                Desative para manter as pendências sem envios automáticos.
              </span>
            </div>

            <input
              type="checkbox"
              checked={ativa}
              onChange={(event) =>
                setAtiva(event.target.checked)
              }
              disabled={saving}
            />
          </label>

          {formError ? (
            <div className="cp-form-error" role="alert">
              <AlertCircle size={16} />
              {formError}
            </div>
          ) : null}

          <div className="cp-regua-editor__head">
            <div>
              <strong>Etapas de envio</strong>
              <span>
                Defina quantas vezes enviar, em qual D+ e em qual horário.
              </span>
            </div>

            <button
              className="cp-btn cp-btn--ghost"
              type="button"
              onClick={adicionarEtapa}
              disabled={saving}
            >
              <Plus size={15} />
              Adicionar etapa
            </button>
          </div>

          <div className="cp-regua-list">
            {etapas.map((etapa, index) => (
              <article
                className="cp-regua-item"
                key={etapa._id || `etapa-${index}`}
              >
                <div className="cp-regua-item__head">
                  <div>
                    <span>Etapa {index + 1}</span>
                    <strong>D+{etapa.diasAposSessao || 0}</strong>
                  </div>

                  <div className="cp-regua-item__tools">
                    <button
                      type="button"
                      onClick={() => moverEtapa(index, -1)}
                      disabled={saving || index === 0}
                      aria-label="Mover etapa para cima"
                      title="Mover para cima"
                    >
                      <ArrowUp size={15} />
                    </button>

                    <button
                      type="button"
                      onClick={() => moverEtapa(index, 1)}
                      disabled={
                        saving ||
                        index === etapas.length - 1
                      }
                      aria-label="Mover etapa para baixo"
                      title="Mover para baixo"
                    >
                      <ArrowDown size={15} />
                    </button>

                    <button
                      type="button"
                      onClick={() => removerEtapa(index)}
                      disabled={saving}
                      aria-label="Remover etapa"
                      title="Remover etapa"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                <div className="cp-regua-item__grid">
                  <label className="cp-field">
                    <span>Dias após a sessão</span>

                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={etapa.diasAposSessao}
                      onChange={(event) =>
                        atualizarEtapa(
                          index,
                          "diasAposSessao",
                          event.target.value
                        )
                      }
                      disabled={saving}
                    />
                  </label>

                  <label className="cp-field">
                    <span>Horário</span>

                    <input
                      type="time"
                      value={etapa.horario}
                      onChange={(event) =>
                        atualizarEtapa(
                          index,
                          "horario",
                          event.target.value
                        )
                      }
                      disabled={saving}
                    />
                  </label>

                  <label className="cp-switch-row cp-switch-row--compact">
                    <div>
                      <strong>Etapa ativa</strong>
                    </div>

                    <input
                      type="checkbox"
                      checked={etapa.ativa !== false}
                      onChange={(event) =>
                        atualizarEtapa(
                          index,
                          "ativa",
                          event.target.checked
                        )
                      }
                      disabled={saving}
                    />
                  </label>
                </div>

                <label className="cp-field">
                  <span>Assunto</span>

                  <input
                    value={etapa.assunto}
                    onChange={(event) =>
                      atualizarEtapa(
                        index,
                        "assunto",
                        event.target.value
                      )
                    }
                    maxLength={160}
                    disabled={saving}
                  />
                </label>

                <label className="cp-field">
                  <span>Mensagem</span>

                  <textarea
                    value={etapa.mensagem}
                    onChange={(event) =>
                      atualizarEtapa(
                        index,
                        "mensagem",
                        event.target.value
                      )
                    }
                    rows={3}
                    maxLength={3000}
                    disabled={saving}
                  />
                </label>
              </article>
            ))}
          </div>
        </div>

        <div className="cp-modal__actions">
          <button
            className="cp-btn cp-btn--ghost"
            type="button"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>

          <button
            className="cp-btn cp-btn--primary"
            type="button"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <LoaderCircle
                  size={16}
                  className="cp-spin"
                />
                Salvando...
              </>
            ) : (
              <>
                <Save size={16} />
                Salvar régua
              </>
            )}
          </button>
        </div>
      </section>
    </div>
  );
}

function PendenciaCard({
  pendencia,
  onMarcarPago,
  onPausar,
  onRetomar,
  onCancelar,
  onEnviarAgora,
  busy,
  hasPremiumAutomation,
}) {
  const status = String(pendencia.status || "")
    .trim()
    .toUpperCase();

  /*
   * Cancelar ou encerrar a régua não significa que a sessão foi paga.
   * Enquanto o status não for PAGA, o usuário ainda pode informar o pagamento.
   */
  const podePagar = status !== "PAGA";
  const podePausar =
    hasPremiumAutomation && status === "ABERTA";
  const podeRetomar =
    hasPremiumAutomation && status === "PAUSADA";
  const podeCancelar =
    status === "ABERTA" || status === "PAUSADA";

  return (
    <article
      className={`cp-card cp-card--${status.toLowerCase()}`}
    >
      <div className="cp-card__top">
        <div className="cp-card__client">
          <div className="cp-avatar">
            {(pendencia.clienteNome || "C")
              .slice(0, 2)
              .toUpperCase()}
          </div>

          <div>
            <strong>
              {pendencia.clienteNome || "Cliente"}
            </strong>

            <span>
              {pendencia.clienteEmail ||
                pendencia.clienteTelefone ||
                "Canais definidos pelo cadastro do cliente"}
            </span>
          </div>
        </div>

        <span
          className={`cp-status cp-status--${status.toLowerCase()}`}
        >
          {getStatusLabel(status)}
        </span>
      </div>

      <div className="cp-card__grid">
        <div>
          <span>Valor</span>
          <strong>
            {formatMoneyBR(pendencia.valor)}
          </strong>
        </div>

        <div>
          <span>Sessão</span>
          <strong>
            {formatDateBR(pendencia.dataSessao)}
          </strong>
        </div>

        <div>
          <span>Régua</span>
          <strong>{getEtapaLabel(pendencia)}</strong>
        </div>

        <div>
          <span>Próximo envio</span>
          <strong>
            {pendencia.proximoEnvioEm
              ? formatDateTimeBR(
                  pendencia.proximoEnvioEm
                )
              : "—"}
          </strong>
        </div>
      </div>

      {pendencia.ultimoErro ? (
        <div className="cp-alert">
          <AlertCircle size={16} />
          <span>
            <strong>Erro no último envio:</strong>{" "}
            {pendencia.ultimoErro}
          </span>
        </div>
      ) : null}

      <div className="cp-card__footer">
        <small>
          Envio(s): {pendencia.totalEnvios || 0}
          {" • "}
          Último envio:{" "}
          {pendencia.ultimoEnvioEm
            ? formatDateTimeBR(pendencia.ultimoEnvioEm)
            : "—"}
        </small>

        <div className="cp-actions">
          {hasPremiumAutomation && podePagar ? (
            <button
              className="cp-btn cp-btn--primary"
              type="button"
              onClick={() => onEnviarAgora(pendencia)}
              disabled={busy}
              title="Enviar cobrança pelos canais disponíveis"
            >
              <Send size={15} />
              Enviar cobrança
            </button>
          ) : null}

          {podePagar ? (
            <button
              className="cp-btn cp-btn--success"
              type="button"
              onClick={() => onMarcarPago(pendencia)}
              disabled={busy}
            >
              <CheckCircle2 size={15} />
              Marcar pago
            </button>
          ) : null}

          {podePausar ? (
            <button
              className="cp-btn cp-btn--ghost"
              type="button"
              onClick={() => onPausar(pendencia)}
              disabled={busy}
            >
              <Pause size={15} />
              Pausar cobrança
            </button>
          ) : null}

          {podeRetomar ? (
            <button
              className="cp-btn cp-btn--ghost"
              type="button"
              onClick={() => onRetomar(pendencia)}
              disabled={busy}
            >
              <Play size={15} />
              Retomar régua
            </button>
          ) : null}

          {podeCancelar ? (
            <button
              className="cp-btn cp-btn--danger"
              type="button"
              onClick={() => onCancelar(pendencia)}
              disabled={busy}
            >
              <Ban size={15} />
              Cancelar
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function ConfirmActionModal({
  action,
  loading,
  onClose,
  onConfirm,
}) {
  if (!action) return null;

  const isDanger = action.tone === "danger";

  return (
    <div
      className="cp-modal-backdrop"
      role="presentation"
      onMouseDown={() => {
        if (!loading) onClose();
      }}
    >
      <section
        className="cp-modal cp-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-confirm-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="cp-confirm__icon">
          {isDanger ? (
            <AlertCircle size={22} />
          ) : (
            <Send size={22} />
          )}
        </div>

        <div className="cp-confirm__content">
          <span>{action.eyebrow || "Confirmação"}</span>

          <h2 id="cp-confirm-title">
            {action.title}
          </h2>

          <p>{action.message}</p>
        </div>

        <div className="cp-modal__actions">
          <button
            className="cp-btn cp-btn--ghost"
            type="button"
            onClick={onClose}
            disabled={loading}
          >
            Voltar
          </button>

          <button
            className={`cp-btn ${
              isDanger
                ? "cp-btn--danger-solid"
                : "cp-btn--primary"
            }`}
            type="button"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <LoaderCircle
                  size={16}
                  className="cp-spin"
                />
                Processando...
              </>
            ) : (
              action.confirmLabel
            )}
          </button>
        </div>
      </section>
    </div>
  );
}

export default function CobrancaPendenciasScreen() {
  const navigate = useNavigate();
  const toast = useToast();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [status, setStatus] = useState("PENDENTES");
  const [pendencias, setPendencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [modalPendencia, setModalPendencia] = useState(null);
  const [configuracao, setConfiguracao] = useState(null);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const nomeUsuario = useMemo(() => readAuth(AUTH_KEYS.USER_NAME) || "Usuário", []);
  const hasPremiumAutomation = useMemo(() => canUseReguaAutomatica(), []);

  const authOrRedirect = useCallback(() => {
    const token = readAuth(AUTH_KEYS.TOKEN);
    const userId = readAuth(AUTH_KEYS.USER_ID);

    if (!token || !userId) {
      toast.error({
        title: "Sessão encerrada",
        message: "Sua sessão expirou. Faça login novamente.",
        duration: 6500,
      });
      navigate("/login", { replace: true });
      return null;
    }

    return { token, userId };
  }, [navigate, toast]);

  const apiFetch = useCallback(
    async (url, options = {}) => {
      const auth = authOrRedirect();

      if (!auth) {
        throw new Error("Usuário não autenticado.");
      }

      const response = await fetch(url, {
        ...options,
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json",
          ...(options.headers || {}),
        },
      });

      saveAuthToken(response.headers.get("x-new-token"));

      if (response.status === 401) {
        toast.error({
        title: "Sessão encerrada",
        message: "Sua sessão expirou. Faça login novamente.",
        duration: 6500,
      });
        navigate("/login", { replace: true });
        throw new Error("Sessão expirada.");
      }

      if (!response.ok) {
        const message = await readErrorMessage(response);
        throw new Error(message);
      }

      return await response.json();
    },
    [authOrRedirect, navigate, toast]
  );

  const carregarPendencias = useCallback(async () => {
    const auth = authOrRedirect();
    if (!auth) return;

    setLoading(true);

    try {
      const params = new URLSearchParams();
      params.set("id", auth.userId);

      if (status) {
        params.set("status", status);
      }

      const data = await apiFetch(`${API_BASE}/psy/cobranca/pendencias?${params.toString()}`);
      setPendencias(Array.isArray(data?.pendencias) ? data.pendencias : []);
    } catch (error) {
      console.error("Erro ao carregar cobranças:", error);
      toast.error({
        title: "Pendências indisponíveis",
        message:
          error.message ||
          "Não foi possível carregar as pendências.",
      });
      setPendencias([]);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, authOrRedirect, status, toast]);

  const carregarConfiguracao = useCallback(async () => {
    if (!hasPremiumAutomation) return;

    const auth = authOrRedirect();
    if (!auth) return;

    setConfigLoading(true);

    try {
      const data = await apiFetch(
        `${API_BASE}/psy/cobranca/configuracao?id=${encodeURIComponent(auth.userId)}`
      );
      setConfiguracao(data?.configuracao || null);
    } catch (error) {
      console.error("Erro ao carregar configuração da régua:", error);
      toast.error({
        title: "Régua indisponível",
        message:
          error.message ||
          "Não foi possível carregar a régua de cobrança.",
      });
    } finally {
      setConfigLoading(false);
    }
  }, [apiFetch, authOrRedirect, hasPremiumAutomation, toast]);

  useEffect(() => {
    carregarPendencias();
  }, [carregarPendencias]);

  useEffect(() => {
    if (hasPremiumAutomation) carregarConfiguracao();
  }, [carregarConfiguracao, hasPremiumAutomation]);

  const resumo = useMemo(() => {
    return pendencias.reduce(
      (acc, item) => {
        const statusItem = String(item.status || "").toUpperCase();

        acc.total += 1;
        acc.valor += Number(item.valor || 0);

        if (statusItem === "ABERTA") acc.abertas += 1;
        if (statusItem === "PAUSADA") acc.pausadas += 1;
        if (statusItem === "PAGA") acc.pagas += 1;

        return acc;
      },
      {
        total: 0,
        abertas: 0,
        pausadas: 0,
        pagas: 0,
        valor: 0,
      }
    );
  }, [pendencias]);

  const processarAgora = async () => {
    if (!hasPremiumAutomation) {
      toast.info({
        title: "Recurso Premium",
        message:
          "A régua automática de cobrança está disponível no plano Premium.",
      });
      return;
    }

    setBusy(true);

    try {
      const auth = authOrRedirect();
      if (!auth) return;

      const data = await apiFetch(
        `${API_BASE}/psy/cobranca/processar?id=${encodeURIComponent(auth.userId)}`,
        {
          method: "POST",
          body: JSON.stringify({ limit: 50 }),
        }
      );

      toast.success({
        title: "Régua processada",
        message: `Processamento concluído. Total: ${data.total || 0}.`,
      });
      await carregarPendencias();
    } catch (error) {
      console.error("Erro ao processar cobrança:", error);
      toast.error({
        title: "Falha no processamento",
        message:
          error.message ||
          "Não foi possível processar as cobranças.",
      });
    } finally {
      setBusy(false);
    }
  };

  const salvarConfiguracao = async (payload) => {
    setBusy(true);

    try {
      const auth = authOrRedirect();
      if (!auth) return;

      const data = await apiFetch(
        `${API_BASE}/psy/cobranca/configuracao?id=${encodeURIComponent(auth.userId)}`,
        {
          method: "PUT",
          body: JSON.stringify(payload),
        }
      );

      setConfiguracao(data?.configuracao || null);
      setConfigModalOpen(false);
      toast.success({
        title: "Régua salva",
        message: "A configuração de cobrança foi atualizada.",
      });
      await carregarPendencias();
    } catch (error) {
      console.error("Erro ao salvar régua:", error);
      toast.error({
        title: "Régua não salva",
        message:
          error.message ||
          "Não foi possível salvar a régua.",
      });
    } finally {
      setBusy(false);
    }
  };

  const executarEnvioAgora = async (pendencia) => {
    setBusy(true);

    try {
      const auth = authOrRedirect();
      if (!auth) return;

      const data = await apiFetch(
        `${API_BASE}/psy/cobranca/pendencias/${getObjectId(
          pendencia._id
        )}/enviar?id=${encodeURIComponent(auth.userId)}`,
        {
          method: "POST",
          body: JSON.stringify({}),
        }
      );

      const feedback = buildCommunicationFeedback(
        data,
        data?.message || "Cobrança processada pelos canais disponíveis."
      );

      const notify = toast[feedback.tone] || toast.success;

      notify({
        title:
          feedback.tone === "warning"
            ? "Cobrança enviada com atenção"
            : feedback.tone === "error"
              ? "Falha nos canais de cobrança"
              : "Cobrança enviada",
        message:
          feedback.message ||
          data?.message ||
          "Cobrança processada pelos canais disponíveis.",
      });

      await carregarPendencias();
    } catch (error) {
      console.error("Erro ao enviar cobrança:", error);

      toast.error({
        title: "Cobrança não enviada",
        message:
          error.message ||
          "Falha ao enviar a cobrança.",
      });
    } finally {
      setBusy(false);
    }
  };

  const enviarAgora = (pendencia) => {
    if (!hasPremiumAutomation) {
      toast.info({
        title: "Recurso Premium",
        message:
          "O envio automático de cobrança está disponível no plano Premium.",
      });
      return;
    }

    setConfirmAction({
      eyebrow: "Envio manual",
      title: "Enviar cobrança agora?",
      message: `O PsyAssist processará a cobrança de ${
        pendencia.clienteNome || "o cliente"
      } pelos canais disponíveis. E-mail e WhatsApp funcionam de forma independente.`,
      confirmLabel: "Enviar cobrança",
      tone: "primary",
      run: () => executarEnvioAgora(pendencia),
    });
  };

  const executarPausa = async (pendencia) => {
    setBusy(true);

    try {
      await apiFetch(
        `${API_BASE}/psy/cobranca/pendencias/${getObjectId(
          pendencia._id
        )}/pausar`,
        {
          method: "PATCH",
          body: JSON.stringify({
            motivo: "Pausada pelo usuário.",
          }),
        }
      );

      toast.success({
        title: "Régua pausada",
        message:
          "Os lembretes desta pendência foram pausados.",
      });

      await carregarPendencias();
    } catch (error) {
      toast.error({
        title: "Não foi possível pausar",
        message:
          error.message ||
          "Falha ao pausar a pendência.",
      });
    } finally {
      setBusy(false);
    }
  };

  const pausarPendencia = (pendencia) => {
    if (!hasPremiumAutomation) {
      toast.info({
        title: "Recurso Premium",
        message:
          "Pausar a régua automática está disponível no plano Premium.",
      });
      return;
    }

    setConfirmAction({
      eyebrow: "Automação",
      title: "Pausar esta régua?",
      message:
        "Nenhuma nova cobrança automática será enviada enquanto esta pendência estiver pausada.",
      confirmLabel: "Pausar cobrança",
      tone: "primary",
      run: () => executarPausa(pendencia),
    });
  };

  const retomarPendencia = async (pendencia) => {
    if (!hasPremiumAutomation) {
      toast.info({
        title: "Recurso Premium",
        message:
          "Retomar a régua automática está disponível no plano Premium.",
      });
      return;
    }

    setBusy(true);

    try {
      await apiFetch(`${API_BASE}/psy/cobranca/pendencias/${getObjectId(pendencia._id)}/retomar`, {
        method: "PATCH",
        body: JSON.stringify({}),
      });

      toast.success({
        title: "Régua retomada",
        message:
          "Os lembretes automáticos desta pendência foram retomados.",
      });
      await carregarPendencias();
    } catch (error) {
      toast.error({
        title: "Não foi possível retomar",
        message:
          error.message ||
          "Falha ao retomar a pendência.",
      });
    } finally {
      setBusy(false);
    }
  };

  const executarCancelamento = async (pendencia) => {
    setBusy(true);

    try {
      await apiFetch(
        `${API_BASE}/psy/cobranca/pendencias/${getObjectId(
          pendencia._id
        )}/cancelar`,
        {
          method: "PATCH",
          body: JSON.stringify({
            motivo: "Cancelada pelo usuário.",
          }),
        }
      );

      toast.success({
        title: "Pendência cancelada",
        message:
          "A cobrança deixou de ficar ativa na régua.",
      });

      await carregarPendencias();
    } catch (error) {
      toast.error({
        title: "Não foi possível cancelar",
        message:
          error.message ||
          "Falha ao cancelar a pendência.",
      });
    } finally {
      setBusy(false);
    }
  };

  const cancelarPendencia = (pendencia) => {
    setConfirmAction({
      eyebrow: "Ação de cobrança",
      title: "Cancelar esta pendência?",
      message:
        "Ela deixará de aparecer como cobrança aberta. Isso não registra o pagamento da sessão.",
      confirmLabel: "Cancelar pendência",
      tone: "danger",
      run: () => executarCancelamento(pendencia),
    });
  };

  const confirmarAcao = async () => {
    const action = confirmAction;

    if (!action?.run || busy) return;

    setConfirmAction(null);
    await action.run();
  };

  const confirmarPagamento = async (payload) => {
    const pendencia = modalPendencia;

    if (!pendencia) return;

    const sessaoId = getObjectId(pendencia.sessaoId);

    if (!sessaoId) {
      toast.error({
        title: "Sessão não identificada",
        message:
          "Não foi possível localizar a sessão vinculada à pendência.",
      });
      return;
    }

    setBusy(true);

    try {
      const auth = authOrRedirect();
      if (!auth) return;

      const data = await apiFetch(
        `${API_BASE}/psy/session/${encodeURIComponent(sessaoId)}/marcar-pago`,
        {
          method: "PATCH",
          body: JSON.stringify(payload),
        }
      );

      const feedback = buildCommunicationFeedback(
        data,
        "O pagamento foi informado e o financeiro será atualizado."
      );

      const notify = toast[feedback.tone] || toast.success;

      notify({
        title:
          feedback.hasChannels && feedback.tone === "warning"
            ? "Pagamento confirmado com atenção"
            : "Pagamento confirmado",
        message: feedback.hasChannels
          ? `Pagamento registrado. Recibo — ${feedback.message}`
          : "O pagamento foi informado e o financeiro será atualizado.",
      });
      setModalPendencia(null);
      await carregarPendencias();
    } catch (error) {
      console.error("Erro ao informar pagamento:", error);
      toast.error({
        title: "Pagamento não confirmado",
        message:
          error.message ||
          "Falha ao informar o pagamento.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cp-page">
      <div className="cp-bg" aria-hidden="true">
        <span className="cp-bg__orb cp-bg__orb--one" />
        <span className="cp-bg__orb cp-bg__orb--two" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="cp-topbar">
        <button
          className="cp-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} aria-hidden="true" />
        </button>

        <div className="cp-brand">
          <img
            src={Logo}
            alt="PsyAssist"
            className="cp-brand__logo"
          />

          <div>
            <strong>Cobranças</strong>
            <span>
              Financeiro • Pagamentos pendentes • {nomeUsuario}
            </span>
          </div>
        </div>

        <div className="cp-top-actions">
          <button
            className="cp-btn cp-btn--ghost cp-btn--desktop"
            type="button"
            onClick={() => navigate("/finance")}
          >
            <ArrowLeft size={16} />
            Financeiro
          </button>

          <button
            className="cp-btn cp-btn--ghost cp-topbar__refresh"
            type="button"
            onClick={carregarPendencias}
            disabled={loading || busy}
          >
            <RefreshCw
              size={16}
              className={loading ? "cp-spin" : ""}
            />
            Atualizar
          </button>

          {hasPremiumAutomation ? (
            <>
              <button
                className="cp-btn cp-btn--ghost cp-btn--desktop"
                type="button"
                onClick={() => setConfigModalOpen(true)}
                disabled={loading || busy || configLoading}
              >
                <Settings2 size={16} />
                Configurar régua
              </button>

              <button
                className="cp-btn cp-btn--primary"
                type="button"
                onClick={processarAgora}
                disabled={loading || busy}
                title="Processa pendências vencidas agora"
              >
                {busy ? (
                  <LoaderCircle
                    size={16}
                    className="cp-spin"
                  />
                ) : (
                  <Play size={16} />
                )}
                Processar
              </button>
            </>
          ) : null}
        </div>
      </header>

      <main className="cp-main">
        <section className="cp-hero">
          <div className="cp-hero__content">
            <span className="cp-kicker">
              <ShieldCheck size={14} aria-hidden="true" />
              Financeiro operacional
            </span>

            <h1>
              Pagamentos pendentes,
              sem perder o controle.
            </h1>

            <p>
              Acompanhe sessões concluídas sem pagamento e sem receita,
              registre recebimentos posteriores e, no Premium, automatize
              a régua de cobrança com e-mail e WhatsApp em paralelo.
            </p>

            <div className="cp-hero__actions">
              <button
                className="cp-btn cp-btn--hero-primary"
                type="button"
                onClick={carregarPendencias}
                disabled={loading || busy}
              >
                <RefreshCw
                  size={16}
                  className={loading ? "cp-spin" : ""}
                />
                Atualizar pendências
              </button>

              {hasPremiumAutomation ? (
                <button
                  className="cp-btn cp-btn--hero-ghost"
                  type="button"
                  onClick={() => setConfigModalOpen(true)}
                  disabled={configLoading || busy}
                >
                  <Settings2 size={16} />
                  Configurar régua
                </button>
              ) : null}
            </div>
          </div>

          <AutomationCard
            hasPremiumAutomation={hasPremiumAutomation}
            configuracao={configuracao}
            loading={configLoading}
            onConfigure={() => setConfigModalOpen(true)}
            onUpgrade={() => navigate("/plan-management")}
          />
        </section>

        <section className="cp-stats">
          <StatCard
            icon={ReceiptText}
            label="Pendências"
            value={resumo.total}
            hint="filtro atual"
            tone="blue"
          />

          <StatCard
            icon={AlertCircle}
            label="Abertas"
            value={resumo.abertas}
            hint="aguardando pagamento"
            tone="amber"
          />

          <StatCard
            icon={Pause}
            label="Pausadas"
            value={resumo.pausadas}
            hint="régua pausada"
            tone="slate"
          />

          <StatCard
            icon={CircleDollarSign}
            label="Valor"
            value={formatMoneyBR(resumo.valor)}
            hint="filtro atual"
            tone="green"
          />
        </section>

        <section className="cp-panel">
          <div className="cp-panel__head">
            <div>
              <span>Operação financeira</span>
              <strong>Pendências e cobranças</strong>
              <small>
                Use o filtro para revisar a situação dos recebimentos.
              </small>
            </div>

            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              disabled={loading || busy}
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value || "TODAS"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="cp-loading">
              <LoaderCircle size={18} className="cp-spin" />
              Carregando pendências...
            </div>
          ) : pendencias.length ? (
            <div className="cp-list">
              {pendencias.map((pendencia) => (
                <PendenciaCard
                  key={getObjectId(pendencia._id)}
                  pendencia={pendencia}
                  onMarcarPago={setModalPendencia}
                  onPausar={pausarPendencia}
                  onRetomar={retomarPendencia}
                  onCancelar={cancelarPendencia}
                  onEnviarAgora={enviarAgora}
                  busy={busy}
                  hasPremiumAutomation={hasPremiumAutomation}
                />
              ))}
            </div>
          ) : (
            <EmptyState status={status} />
          )}
        </section>
      </main>


      <ReguaConfigModal
        open={configModalOpen}
        configuracao={configuracao}
        saving={busy}
        onClose={() => {
          if (!busy) setConfigModalOpen(false);
        }}
        onSave={salvarConfiguracao}
      />

      <PaymentModal
        open={Boolean(modalPendencia)}
        pendencia={modalPendencia}
        saving={busy}
        onClose={() => {
          if (!busy) setModalPendencia(null);
        }}
        onConfirm={confirmarPagamento}
      />

      <ConfirmActionModal
        action={confirmAction}
        loading={busy}
        onClose={() => {
          if (!busy) setConfirmAction(null);
        }}
        onConfirm={confirmarAcao}
      />
    </div>
  );
}
